/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { produce } from 'immer';
import {
  ActorRefFrom,
  SnapshotFrom,
  assertEvent,
  assign,
  emit,
  enqueueActions,
  fromPromise,
  raise,
  sendTo,
  setup,
} from 'xstate';

import { cacheUtils } from '@eluve/apollo-client';
import { scheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { formatErrorForLogging } from '@eluve/eluve-errors';
import {
  appointmentSegmentsFragment,
  segmentTranscriptFragment,
} from '@eluve/frontend-appointment-hooks';
import {
  logMessage,
  preventBrowserClose,
  toastAction,
} from '@eluve/frontend-machine-utils';
import { keepScreenAwake } from '@eluve/frontend-machine-utils';
import { AppointmentFileTypesLookup } from '@eluve/graphql-types';
import { createCorrelatedLogger } from '@eluve/logger';
import { setupEluveMediaStreams } from '@eluve/mic-utils';
import { getSupportedAudioFormat } from '@eluve/user-local-files';
import {
  createStableUuid,
  getChunkNumberFromFileName,
  isAudioFileChunkFile,
  makeStandardAudioFileName,
} from '@eluve/utils';

import { MuteDetector } from '../../MuteDetector';
import { scheduleFileUploadAction } from '../../actions';
import {
  MediaRecorderEvents,
  MicrophoneEvents,
  PrepareAppointmentAudioRecordsInput,
  PrepareAppointmentAudioRecordsResponse,
  WebSocketEvents,
  audioFileSystem,
  getAuthToken,
  mediaRecorder,
  validateAudioCapture,
  websocket,
} from '../../actors';
import { lockManagerActor } from '../../actors/lockManagerActor';
import { createNewAudioPacketSubject } from '../../utils';

import { getCurrentTranscript } from './getCurrentTranscript';
import {
  BLOB_MODE_BITRATE_BPS,
  logRecordingStorageDecision,
  monitorRecordingStorageActor,
  resolveAudioSourceMode,
} from './recordingStorageCheck';
import {
  AudioHealthEvents,
  Context,
  Input,
  TranscriptionMachineEvents,
} from './types';

export const CONTINUED_USER_PRESENCE_RESPONSE_WINDOW_MS = 1000 * 60; // 1 minute
export const CONTINUED_USER_PRESENCE_TIMEOUT_MS =
  1000 * 60 * 30 - CONTINUED_USER_PRESENCE_RESPONSE_WINDOW_MS; // 30 minutes minus response window
export const INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS = 1000 * 60 * 3; // 3 minutes
export const INITIAL_USER_PRESENCE_TIMEOUT_MS =
  1000 * 60 * 60 * 2 - INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS; // 2 hours minus response window

export const FINALIZING_RECORDING_TIMEOUT_MS = 10;
const FINALIZING_RECORDING_TIMEOUT_FOR_WEBWORKER_MS = 1500;

const WEBSOCKET_ACTOR = 'websocket';
const MEDIA_RECORDER_ACTOR = 'mediaRecorder' as const;

const LOCK_MANAGER_ACTOR = 'lockManagerActor';

const LOW_CONFIDENCE_DIALOGUE_THRESHOLD = 0.8;
const LOW_CONFIDENCE_DIALOGUE_WINDOW_SIZE = 7;

// File-activity watchdog: every healthy recording emits fileSystem.fileCreated
// on start (file allocation) and, when chunked, at every chunk-window start.
// Silence past these budgets means the worker/pipeline is wedged and audio is
// silently not being persisted — surface it via RecordingFailed (which retries
// once with a fresh worker) instead of recording nothing. The delayed raise is
// re-armed by the `fileCreated` action and canceled when Recording exits.
const FILE_WATCHDOG_ID = 'recording-file-watchdog';
const FIRST_FILE_WATCHDOG_MS = 30_000;
// Slack beyond the chunk duration: covers encode backlog plus the bounded
// upload-abort ceilings (30s tail PUT + 45s chunk-close hook) before a roll
// can complete under network failure.
const CHUNK_WATCHDOG_SLACK_MS = 90_000;

// Chunked-batch is only valid on the webworker stack. pcm mode rolls a fresh
// muxer per chunk; blob mode (MediaRecorder fallback) runs its continuous
// byte stream through the worker's StreamingRemuxer, which emits
// self-contained per-chunk files. The LEGACY stack chops MediaRecorder bytes
// at chunk boundaries — undecodable standalone — so it stays excluded.
// Must match the worker's own gate (chunkDurationInSeconds truthy): the
// enable-bool and the duration payload are SEPARATE flags, and with the bool
// on but no duration the worker records a single batch file — treating that
// as chunked would arm the chunk watchdog against files that never come.
const isChunkedBatchActive = (context: Context): boolean =>
  context.chunkedBatchTranscriptionEnabled &&
  context.isUsingWebWorkerAudioRecording &&
  Boolean(context.chunkDurationMinutes);

const buildSegmentMetadata = (context: Context): Record<string, string> => {
  // Prefer the encoder-reported capture params (available once
  // `recorder.started` lands); fall back to the mic track's settings for
  // metadata built before that (e.g. the initial prepare call). getSettings
  // is missing on some legacy tracks — this runs inside invoke inputs, where
  // a throw would kill the whole machine, so never let it propagate.
  const track = context.mediaStream?.getAudioTracks()[0];
  const trackSettings =
    typeof track?.getSettings === 'function' ? track.getSettings() : undefined;
  const sampleRate =
    context.capturedAudioParams?.sampleRate ?? trackSettings?.sampleRate;
  const channels =
    context.capturedAudioParams?.channels ?? trackSettings?.channelCount;

  return {
    audioStack: context.isUsingWebWorkerAudioRecording ? 'webworker' : 'legacy',
    // Report whether chunking actually engaged, not the raw flag — a legacy
    // session with the flag on doesn't chunk, so "true" would be misleading.
    chunkedBatchTranscription: String(isChunkedBatchActive(context)),
    resumableUploads: String(context.isUsingResumableUploads),
    liveTranscription: String(context.isUsingLiveTranscription),
    ...(sampleRate !== undefined
      ? { audioSampleRate: String(sampleRate) }
      : {}),
    ...(channels !== undefined ? { audioChannelCount: String(channels) } : {}),
    ...(context.capturedAudioParams
      ? { audioEncodeMode: context.capturedAudioParams.encodeMode }
      : {}),
  };
};

type EmittedEvents =
  | { type: 'recording.started' }
  | { type: 'recording.failed' }
  | { type: 'recording.stopped-by-storage-block' }
  | { type: 'paused' }
  | { type: 'stopped' }
  | { type: 'idle' }
  | { type: 'storageDecisionChanged' }
  | { type: 'checkingUserPresence' }
  | { type: 'disclaimer' }
  | { type: 'transcription.degraded'; isDegraded: boolean }
  | { type: 'mediaStream.set' }
  | WebSocketEvents
  | MicrophoneEvents
  | AudioHealthEvents;

export const transcriptionMachine = setup({
  types: {
    context: {} as Context,
    events: {} as TranscriptionMachineEvents,
    input: {} as Input,
    emitted: {} as EmittedEvents,
  },
  guards: {
    areStreamsSetup: () => Boolean(window?.eluveMediaStreams),
    hasNonSupportedBrowser: ({ context }) =>
      context.supportedAudioFormat === undefined,
    hasInsufficientStorage: ({ context }) =>
      context.storageDecision === 'block',
    isFirstRecordingFailure: ({ context }) => context.recordingRetryCount <= 1,
    needsToPlayDisclaimer: ({ context }) => Boolean(context.playDisclaimer),
    isInChunkingMode: ({ context }) =>
      Boolean(context.chunkedBatchTranscriptionEnabled),
    isPausing: ({ context }) => context.isPausing,
    isStopping: ({ context }) => context.isStopping,
    pauseFinalized: ({ context }) => {
      const arePendingFilesFinalized =
        context.filesPendingCompletion.length === 0;

      return (
        context.isPausing &&
        context.isRecordingCleanupFinalized &&
        arePendingFilesFinalized
      );
    },
    stopFinalized: ({ context }) => {
      const arePendingFilesFinalized =
        context.filesPendingCompletion.length === 0;

      return (
        context.isStopping &&
        context.isRecordingCleanupFinalized &&
        arePendingFilesFinalized
      );
    },
    isLiveTranscriptionEnabled: ({ context }) =>
      context.isUsingLiveTranscription === true,
    isSameTenant: ({ context, event }) =>
      event.type === 'UPDATE_TENANT' && context.tenantId === event.tenantId,
    hasActiveSession: ({ context }) => context.sessionAppointmentId != null,
  },
  actors: {
    websocket,
    mediaRecorder,
    audioFileSystem,
    preventBrowserClose,
    keepScreenAwake,
    getAuthToken,
    validateAudioCapture,
    monitorRecordingStorage: monitorRecordingStorageActor,
    prepareAppointmentAudioRecords: fromPromise<
      PrepareAppointmentAudioRecordsResponse,
      PrepareAppointmentAudioRecordsInput
    >(async () => {
      return {
        resumableUploadSessionUri: undefined,
        nextResumableUploadSessionUri: undefined,
      };
    }),
    lockManagerActor,
  },
  actions: {
    logMessage,
    updateTenant: assign({
      tenantId: ({ context, event }) =>
        event.type === 'UPDATE_TENANT' ? event.tenantId : context.tenantId,
    }),
    setupMediaStreams: () => {
      if (!window?.eluveMediaStreams) {
        setupEluveMediaStreams();
      }
    },
    incrementRecordingRetryCount: assign({
      recordingRetryCount: ({ context }) => context.recordingRetryCount + 1,
    }),
    toast: toastAction,
    logError: (_machine: unknown, params: { message: string }) => {
      const {
        context: { logger },
      } = _machine as { context: Context };
      logger.error(params.message);
    },

    captureException: ({ event, context }) => {
      if ('error' in event && event.error) {
        const errorMessage = event.error?.message ?? event.error;

        context.logger.error('Recording Exception', {
          error: errorMessage,
          event,
          appointmentId: context.sessionAppointmentId,
          segmentId: context.segmentId,
        });
      }
    },

    captureWarning: ({ event, context }) => {
      const message = (() => {
        if ('message' in event) {
          return event.message;
        }
        if ('error' in event && event.error && event.error instanceof Error) {
          return formatErrorForLogging(event.error);
        }

        return '';
      })();

      context.logger.warn(`Recording Warning: ${message}`, {
        event,
        appointmentId: context.sessionAppointmentId,
        segmentId: context.segmentId,
      });
    },

    onAudioSilent: enqueueActions(({ enqueue, event }) => {
      assertEvent(event, ['audio.silent']);
      enqueue.assign({ hasValidAudioSource: false });
      enqueue.emit(event);
    }),
    onAudioSoundDetected: enqueueActions(({ enqueue, event }) => {
      assertEvent(event, ['audio.soundDetected']);
      enqueue.assign({ hasValidAudioSource: true });
      enqueue.emit(event);
    }),

    setSession: assign(({ event }) => {
      assertEvent(event, ['START']);
      return {
        sessionAppointmentId: event.appointmentId,
        microphoneName: event.microphoneName,
        deviceId: event.deviceId,
        playDisclaimer: event.playDisclaimer,
        recordingFailed: false,
        recordingRetryCount: 0,
        confidenceScoreWindow: [],
        isLowConfidenceTranscription: false,
        totalRecordingDurationMs: event.initialTotalRecordingDurationMs ?? 0,
        // Clear any prior session's "audio may be missing" notice. Kept across
        // RESUME/auto-retry within a session (those re-enter PreparingRecording,
        // not START), so a mid-session empty segment still warns at stop. [ENG-4498]
        audioPotentiallyLost: undefined,
        // New session — no known-good audio yet. [ENG-4498]
        lastKnownGoodHealthCheckAt: undefined,
      };
    }),

    clearSession: assign(() => {
      return {
        sessionAppointmentId: undefined,
        transcript: undefined,
        playDisclaimer: undefined,
        recordingRetryCount: 0,
        recordingFailed: false,
        confidenceScoreWindow: [],
        isLowConfidenceTranscription: false,
        totalRecordingDurationMs: 0,
      };
    }),
    prepareRecording: enqueueActions(({ enqueue, context }) => {
      const recordingStartedAt = new Date().toISOString();

      // Initial file is chunked only on the webworker stack; legacy creates a full batch file here
      const isInitialFileChunked = isChunkedBatchActive(context);

      // Blob mode stores the recorder's container (fMP4 on Safari), so the
      // name must match it — the backend infers MIME from the extension. PCM
      // always muxes WebM; V1 keeps its legacy webm default. Must agree with
      // the worker's own minting (fileExtension in the init message).
      const fileExtension =
        context.isUsingWebWorkerAudioRecording &&
        resolveAudioSourceMode(context.useBlobMode) === 'blob'
          ? (context.supportedAudioFormat?.extension ?? 'webm')
          : 'webm';

      const initialFileName = makeStandardAudioFileName({
        recordingStartedAt,
        isChunkingEnabled: isInitialFileChunked,
        chunkNum: 0,
        extension: fileExtension,
      });

      enqueue.assign({
        recordingFileName: initialFileName,
        recordingStartedAt,
        segmentId: createStableUuid(recordingStartedAt),
        transcript: ({ context }) =>
          getCurrentTranscript(context.sessionAppointmentId!).transcript,
        interimTranscript: '',
        audioPacketSubject: createNewAudioPacketSubject,
        microphoneStatus: undefined,
        // New segment — no local audio file created yet. [ENG-4498]
        segmentAudioFileObserved: false,
      });

      enqueue('updateSegmentCache');
    }),

    // log the free-storage decision (ok/warn/block) on
    // every recording-file (re)creation — covers start, resume, and auto-retry.
    // Fire-and-forget; never blocks or fails the transition.
    logRecordingStorageCheck: ({ context, event }) => {
      // pcm writes opus at the configured bitrate; blob (MediaRecorder) and the
      // legacy recorder write browser-encoded audio (~128 kbps) regardless of
      // opusBitrate, so estimate per mode rather than always assuming opus.
      const recordingMode = context.isUsingWebWorkerAudioRecording
        ? resolveAudioSourceMode(context.useBlobMode)
        : 'blob';

      void logRecordingStorageDecision({
        logger: context.logger,
        appointmentId: context.sessionAppointmentId,
        deviceId: context.appDeviceId, // per-install id; OPFS quota is per-device
        chunkDurationMinutes: context.chunkDurationMinutes,
        bitrateBps:
          recordingMode === 'pcm' ? context.opusBitrate : BLOB_MODE_BITRATE_BPS,
        recordingMode,
        trigger: event.type,
      });
    },

    updateSegmentCache: ({ context }) => {
      const { sessionAppointmentId, segmentId, recordingStartedAt } = context;
      if (sessionAppointmentId && segmentId) {
        cacheUtils.updateFragment(
          {
            fragment: appointmentSegmentsFragment,
            key: {
              id: sessionAppointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }
            return produce(existing, (draft) => {
              draft.segments.push({
                __typename: 'AppointmentSegments' as const,
                id: segmentId,
                transcript: '',
                recordingStartedAt,
                recordingDuration: null,
                updatedAt: new Date().toISOString(),
              });
            });
          },
        );
      }
    },

    setDeviceId: assign(({ context, event }) => {
      assertEvent(event, ['RESUME', 'AUDIO_VALIDATION_REQUESTED']);
      return { deviceId: event.deviceId ?? context.deviceId };
    }),

    setMediaStream: enqueueActions(({ event, enqueue }) => {
      assertEvent(event, ['recorder.started']);
      enqueue.assign({
        mediaStream: event.mediaStream,
        waveformAnalyserNode: event.waveformAnalyserNode,
        capturedAudioParams: event.audioParams,
      });
      enqueue.emit({ type: 'mediaStream.set' });
    }),

    startWebSocket: sendTo(WEBSOCKET_ACTOR, ({ context }) => {
      return { type: 'connection.connect', authToken: context.authToken };
    }),

    onMicrophoneDisconnected: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        microphoneStatus: 'disconnected',
      });
      enqueue.emit({ type: 'microphone.disconnected' });
    }),

    onMicrophoneMuted: enqueueActions(({ enqueue }) => {
      enqueue({
        type: 'toast',
        params: { type: 'warning', message: 'Your microphone is muted.' },
      });
      enqueue.emit({ type: 'microphone.muted' });
      enqueue.assign({ microphoneStatus: 'muted' });
    }),

    onMicrophoneUnmuted: enqueueActions(({ enqueue }) => {
      enqueue.emit({ type: 'microphone.unmuted' });
      enqueue.assign({ microphoneStatus: 'connected' });
    }),

    playDisclaimerSound: enqueueActions(({ enqueue }) => {
      enqueue.emit({ type: 'disclaimer' });
    }),

    // Arm the watchdog for the segment's FIRST file: allocation happens right
    // after worker init, so 30s of silence means the worker/worklet is dead.
    armFileWatchdog: enqueueActions(({ enqueue }) => {
      enqueue.cancel(FILE_WATCHDOG_ID);
      enqueue.raise(
        { type: 'recording.fileWatchdogTimeout' },
        { delay: 'firstFileWatchdog', id: FILE_WATCHDOG_ID },
      );
    }),

    cancelFileWatchdog: enqueueActions(({ enqueue }) => {
      enqueue.cancel(FILE_WATCHDOG_ID);
    }),

    // The worker's remuxer failed and the session degraded to the raw batch
    // file: no more chunk files will be created, so the chunk watchdog must
    // not treat their absence as a wedged pipeline.
    onChunkingDegraded: enqueueActions(({ context, enqueue }) => {
      context.logger.warn(
        'Chunk remux degraded to batch — disarming chunk file watchdog',
        {
          appointmentId: context.sessionAppointmentId,
          segmentId: context.segmentId,
          lastObservedChunkFileName: context.lastObservedChunkFileName,
        },
      );
      enqueue.cancel(FILE_WATCHDOG_ID);
    }),

    onFileWatchdogTripped: ({ context }) => {
      context.logger.error(
        'Recording file-activity watchdog tripped — no file created within the expected window; failing the recording so retry can engage',
        {
          appointmentId: context.sessionAppointmentId,
          segmentId: context.segmentId,
          lastObservedChunkFileName: context.lastObservedChunkFileName,
          segmentAudioFileObserved: context.segmentAudioFileObserved,
          chunkDurationMinutes: context.chunkDurationMinutes,
          chunkedBatchActive: isChunkedBatchActive(context),
          totalRecordingDurationMs: context.totalRecordingDurationMs,
          recordingRetryCount: context.recordingRetryCount,
        },
      );
    },

    // The finalizing escape hatch fired: the worker never acked cleanup and/or
    // files never finalized within the budget, and we force-exited anyway.
    // Without this log the forced path is indistinguishable from a clean stop.
    logForcedFinalizeExit: ({ context }) => {
      // The legacy stack's 10ms finalize budget makes the forced path its
      // NORMAL exit — only the webworker stack treats it as an anomaly.
      if (!context.isUsingWebWorkerAudioRecording) {
        return;
      }
      context.logger.error(
        'Recording finalize timed out — force-exiting with cleanup incomplete',
        {
          appointmentId: context.sessionAppointmentId,
          segmentId: context.segmentId,
          isRecordingCleanupFinalized: context.isRecordingCleanupFinalized,
          filesPendingCompletion: context.filesPendingCompletion,
          finalizingAudioTimeoutInMs: context.finalizingAudioTimeoutInMs,
          isPausing: context.isPausing,
          isStopping: context.isStopping,
        },
      );
    },

    fileCreated: enqueueActions(({ event, context, enqueue }) => {
      assertEvent(event, ['fileSystem.fileCreated']);
      enqueue.assign({
        lastObservedChunkFileName: event.fileName,
        // A local audio file was opened for this segment. Fires on file
        // allocation (before the first packet), so its ABSENCE at finalize is
        // the phantom signal: no file ever created → permanently-stuck upload
        // slot with no blob. [ENG-4498]
        segmentAudioFileObserved: true,
      });

      // Re-arm the file-activity watchdog: chunked sessions must produce the
      // next chunk file within a chunk duration (+ slack); batch sessions
      // create exactly one file, so the watchdog disarms after it.
      enqueue.cancel(FILE_WATCHDOG_ID);
      if (isChunkedBatchActive(context)) {
        enqueue.raise(
          { type: 'recording.fileWatchdogTimeout' },
          { delay: 'chunkFileWatchdog', id: FILE_WATCHDOG_ID },
        );
      }

      enqueue.sendTo(LOCK_MANAGER_ACTOR, event);

      const isChunkedFile = isAudioFileChunkFile(event.fileName);

      if (!context.filesPendingCompletion.includes(event.fileName)) {
        enqueue.assign({
          filesPendingCompletion: [
            ...context.filesPendingCompletion,
            event.fileName,
          ],
        });
      }

      if (isChunkedBatchActive(context) && isChunkedFile) {
        enqueue.assign({
          audioChunkFiles: [
            ...context.audioChunkFiles,
            { fileName: event.fileName, chunkNum: event.chunkNum },
          ],
        });
      }
    }),

    processCompletedChunk: enqueueActions(({ context, event, enqueue }) => {
      assertEvent(event, ['fileSystem.fileReady']);

      enqueue.sendTo(LOCK_MANAGER_ACTOR, event);

      enqueue({
        type: 'scheduleFileUpload',
        params: {
          type: 'TASK.UPLOAD_FILE',
          uploadType: isAudioFileChunkFile(event.fileName)
            ? AppointmentFileTypesLookup.CHUNKED_AUDIO
            : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
          chunkDurationMinutes: context.chunkDurationMinutes,
          appointmentId: context.sessionAppointmentId!,
          fileName: event.fileName,
          chunkNum: event.chunkNum,
          format: context.supportedAudioFormat!,
          wasDegraded: context.isTranscriptionDegraded ?? false,
          shouldBeTranscribed: true,
          recordingStartedAt: context.recordingStartedAt,
          attemptNumber: 1,
        },
      });

      enqueue.assign({
        audioChunkFiles: context.audioChunkFiles.filter(
          (chunkFile) => chunkFile.fileName !== event.fileName,
        ),
        filesPendingCompletion: context.filesPendingCompletion.filter(
          (fileName) => fileName !== event.fileName,
        ),
      });
    }),

    scheduleFileUpload: scheduleFileUploadAction,
    scheduleBackgroundTask: scheduleBackgroundTaskAction,

    stopRecording: enqueueActions(({ enqueue, context }) => {
      context.mediaStream?.getTracks().forEach((track) => track.stop());
      context.audioPacketSubject?.complete();

      if (context.lastObservedChunkFileName) {
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.MARK_CHUNK_FINAL',
            appointmentId: context.sessionAppointmentId!,
            fileName: context.lastObservedChunkFileName!,
            userId: context.userId,
          },
        });
      }

      // [ENG-4498] `segmentAudioFileObserved` is set on `fileSystem.fileCreated`
      // (file allocation) by the only code that creates a local audio file, so
      // its falsiness means no local file was ever created for this segment —
      // nothing to upload. Scheduling the normal upload for such a slot leaves
      // its reserved `PENDING_UPLOAD` row pending forever and permanently blocks
      // note generation. Drive it to a terminal resolve instead, which the
      // backend reconciles against GCS (recover if bytes somehow exist, cancel
      // if genuinely empty) — never a blind cancel.
      const segmentObservedLocalFile =
        context.segmentAudioFileObserved === true;

      if (
        context.recordingFileName &&
        context.sessionAppointmentId &&
        segmentObservedLocalFile
      ) {
        enqueue({
          type: 'scheduleFileUpload',
          params: {
            type: 'TASK.UPLOAD_FILE',
            uploadType: isAudioFileChunkFile(context.recordingFileName)
              ? AppointmentFileTypesLookup.CHUNKED_AUDIO
              : AppointmentFileTypesLookup.USER_BACKUP_AUDIO,
            appointmentId: context.sessionAppointmentId,
            fileName: context.recordingFileName,
            format: context.supportedAudioFormat!,
            wasDegraded: context.isTranscriptionDegraded ?? false,
            shouldBeTranscribed:
              // Transcribe the full file unless chunked-batch is actually active
              // (webworker stack). Legacy never chunks, so its full file is transcribed.
              Boolean(
                context.isUsingWebWorkerAudioRecording ||
                  !isChunkedBatchActive(context),
              ),
            recordingStartedAt: context.recordingStartedAt,
            attemptNumber: 1,
          },
        });
      } else if (
        context.recordingFileName &&
        context.sessionAppointmentId &&
        context.segmentId &&
        context.recordingStartedAt
      ) {
        // No local audio file was ever observed — resolve the reserved slot.
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.RESOLVE_EMPTY_SEGMENT',
            appointmentId: context.sessionAppointmentId,
            segmentId: context.segmentId,
            fileName: context.recordingFileName,
            userId: context.userId,
            recordingStartedAt: context.recordingStartedAt,
          },
        });

        // Surface a post-session notification: we recorded but captured no
        // audio (likely device sleep). The lost window is measured from the last
        // moment audio was actually flowing this session
        // (`lastKnownGoodHealthCheckAt`) — that's the real sleep gap when a
        // session recorded fine and then slept. If audio never flowed at all,
        // fall back to the segment start. Survives the session-end reset below
        // (which does not touch `audioPotentiallyLost`).
        const detectedAtMs = Date.now();
        const lostWindowReferenceMs = new Date(
          context.lastKnownGoodHealthCheckAt ?? context.recordingStartedAt,
        ).getTime();
        const lostWindowMs = Math.max(0, detectedAtMs - lostWindowReferenceMs);
        enqueue.assign({
          audioPotentiallyLost: {
            appointmentId: context.sessionAppointmentId,
            segmentId: context.segmentId,
            recordingStartedAt: context.recordingStartedAt,
            lastKnownGoodHealthCheckAt: context.lastKnownGoodHealthCheckAt,
            errorDetectedAt: new Date(detectedAtMs).toISOString(),
            lostWindowMs,
          },
        });
      } else {
        // [ENG-4498] Neither upload nor resolve is scheduled here, and that's
        // correct: without a recordingFileName + sessionAppointmentId +
        // segmentId + recordingStartedAt, no backend upload slot was ever
        // reserved for this segment, so there is nothing to upload and nothing
        // to reconcile. We only log it as an unexpected state.
        enqueue({
          type: 'logError',
          params: {
            message:
              'No recording file name or appointment ID available when stopping recording',
          },
        });
      }

      enqueue({
        type: 'scheduleBackgroundTask',
        params: {
          type: 'TASK.UPDATE_SEGMENT_RECORDING_ENDED_AT',
          appointmentId: context.sessionAppointmentId!,
          segmentId: context.segmentId!,
          recordingEndedAt: new Date().toISOString(),
        },
      });

      for (const chunkFile of context.audioChunkFiles) {
        enqueue({
          type: 'scheduleFileUpload',
          params: {
            uploadType: AppointmentFileTypesLookup.CHUNKED_AUDIO,
            chunkDurationMinutes: context.chunkDurationMinutes,
            type: 'TASK.UPLOAD_FILE',
            appointmentId: context.sessionAppointmentId!,
            fileName: chunkFile.fileName,
            chunkNum: chunkFile.chunkNum,
            format: context.supportedAudioFormat!,
            wasDegraded: context.isTranscriptionDegraded ?? false,
            shouldBeTranscribed: true,
            recordingStartedAt: context.recordingStartedAt,
            attemptNumber: 1,
          },
        });
      }

      enqueue.emit({
        type: 'transcription.degraded',
        isDegraded: false,
      });

      enqueue.assign({
        segmentId: undefined,
        mediaStream: undefined,
        waveformAnalyserNode: undefined,
        interimTranscript: undefined,
        authToken: undefined,
        recordingFileName: undefined,
        isTranscriptionDegraded: undefined,
        audioPacketSubject: undefined,
        microphoneStatus: undefined,
        isPausing: false,
        isStopping: false,
        filesPendingCompletion: [],
        audioChunkFiles: [],
        isRecordingCleanupFinalized: false,
      });
    }),

    updateTranscription: assign(({ context, event }) => {
      assertEvent(event, ['connection.transcript.received']);
      const { segmentId } = context;

      const {
        transcript: { isFinal, transcript },
      } = event;

      if (isFinal) {
        if (segmentId) {
          // Push the new transcript into Apollo cache so it can be consumed by other
          // components
          cacheUtils.updateFragment(
            {
              fragment: segmentTranscriptFragment,
              key: {
                id: segmentId,
              },
            },
            (existing) => {
              return {
                __typename: 'AppointmentSegments' as const,
                id: segmentId,
                transcript: [existing?.transcript, transcript]
                  .filter(Boolean)
                  .join(' ')
                  .trim(),
                updatedAt: new Date().toISOString(),
              };
            },
          );
        }

        return {
          transcript: [context.transcript, transcript]
            .filter(Boolean)
            .join(' '),
          interimTranscript: '',
        };
      }

      return {
        interimTranscript: transcript,
      };
    }),

    updateLowConfidenceState: assign(({ context, event }) => {
      assertEvent(event, ['connection.transcript.received']);

      const confidence = event.transcript?.confidence;
      const prevWindow = context.confidenceScoreWindow ?? [];

      const newWindow =
        typeof confidence === 'number'
          ? [...prevWindow, confidence].slice(
              -LOW_CONFIDENCE_DIALOGUE_WINDOW_SIZE,
            )
          : prevWindow;

      const isLowConfidenceTranscription =
        newWindow.length === LOW_CONFIDENCE_DIALOGUE_WINDOW_SIZE &&
        newWindow.every((score) => score < LOW_CONFIDENCE_DIALOGUE_THRESHOLD);

      return {
        confidenceScoreWindow: newWindow,
        isLowConfidenceTranscription,
      };
    }),

    setTranscriptionDegraded: enqueueActions(({ enqueue, context, event }) => {
      context.logger.warn('Live Transcription degraded', {
        appointmentId: context.sessionAppointmentId,
        segmentId: context.segmentId,
        event,
      });

      enqueue.emit({
        type: 'transcription.degraded',
        isDegraded: true,
      });

      enqueue.assign({
        isTranscriptionDegraded: true,
      });
    }),

    onRecordingFailed: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        recordingFailed: true,
      });
      enqueue.emit({ type: 'recording.failed' });
    }),

    shortenUserPresenceDelays: assign({
      userPresenceResponseWindowMs: CONTINUED_USER_PRESENCE_RESPONSE_WINDOW_MS,
      userPresenceTimeoutMs: CONTINUED_USER_PRESENCE_TIMEOUT_MS,
    }),

    resetUserPresenceDelays: assign({
      userPresenceResponseWindowMs: INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS,
      userPresenceTimeoutMs: INITIAL_USER_PRESENCE_TIMEOUT_MS,
    }),

    logStartStopEvents: ({ context }) => {
      const now = Date.now();
      if (context.recordingStartedAt) {
        const elapsed = now - new Date(context.recordingStartedAt).getTime();
        if (elapsed < 1000) {
          context.logger.warn('Transcription stopped in less than 1 second', {
            elapsed,
            appointmentId: context.sessionAppointmentId,
          });
        } else if (elapsed < 3000) {
          context.logger.info('Transcription stopped in less than 3 seconds', {
            elapsed,
            appointmentId: context.sessionAppointmentId,
          });
        }
      }
    },
    // [ENG-4498] Diagnostic: record how each segment+upload-slot is provisioned.
    // Entering `PreparingRecording` always mints a new segment and provisions a
    // backend upload slot, regardless of whether any audio is later captured.
    // Logging the triggering event lets us identify, in prod, which path
    // (user RESUME, mic-disconnect dialog resume, RECONNECT, first-failure
    // auto-retry, restart, etc.) creates the zero-audio "phantom" segments that
    // permanently block summarization. Correlate by `segmentId`/`recordingStartedAt`
    // with the matching `Segment finalized` log.
    logSegmentProvisioned: ({ context, event }) => {
      context.logger.info('Segment provisioned', {
        appointmentId: context.sessionAppointmentId,
        segmentId: context.segmentId,
        recordingStartedAt: context.recordingStartedAt,
        trigger: event.type,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        microphoneStatus: context.microphoneStatus,
        recordingRetryCount: context.recordingRetryCount,
        recordingFailed: context.recordingFailed,
        hasValidAudioSource: context.hasValidAudioSource,
        isTranscriptionDegraded: context.isTranscriptionDegraded,
        deviceId: context.appDeviceId,
      });
    },

    // [ENG-4498] Stamp the last moment audio was genuinely flowing (a passing
    // health check). Session-level — used to measure a later device-sleep gap.
    recordHealthyAudioCheck: assign(() => ({
      lastKnownGoodHealthCheckAt: new Date().toISOString(),
    })),

    // [ENG-4498] Diagnostic: when a segment finalizes (Recording exit), warn if
    // it never created a local audio file — that segment reserved a
    // `PENDING_UPLOAD`/`PENDING_AUDIO` slot with no blob, which
    // TASK.RESOLVE_EMPTY_SEGMENT must clean up [ENG-4709]. Captures the
    // surrounding state (online/mic/degraded/stop trigger) so root causes for
    // zero-audio segments stay visible.
    logSegmentFinalized: ({ context, event }) => {
      const elapsedMs = context.recordingStartedAt
        ? Date.now() - new Date(context.recordingStartedAt).getTime()
        : undefined;
      // Tracks file allocation (`fileSystem.fileCreated`), not per-packet
      // capture — its ABSENCE means no local file was ever created, the
      // signature of a permanently-stuck phantom slot.
      const fileObserved = context.segmentAudioFileObserved ?? false;
      const payload = {
        appointmentId: context.sessionAppointmentId,
        segmentId: context.segmentId,
        recordingStartedAt: context.recordingStartedAt,
        elapsedMs,
        segmentAudioFileObserved: fileObserved,
        audioChunkFileCount: context.audioChunkFiles?.length ?? 0,
        // Stale across segments — only meaningful when this segment created a
        // file; otherwise it's a carryover from a prior segment.
        lastObservedChunkFileName: fileObserved
          ? context.lastObservedChunkFileName
          : undefined,
        hasValidAudioSource: context.hasValidAudioSource,
        isTranscriptionDegraded: context.isTranscriptionDegraded,
        microphoneStatus: context.microphoneStatus,
        recordingRetryCount: context.recordingRetryCount,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
        stopTrigger: event.type,
        deviceId: context.appDeviceId,
      };
      if (fileObserved) {
        context.logger.info('Segment finalized', payload);
      } else {
        // No local audio file was ever created for this segment, so its
        // PENDING_UPLOAD/PENDING_AUDIO slot has no blob and can never complete.
        // `stopRecording` schedules TASK.RESOLVE_EMPTY_SEGMENT, which reconciles
        // the slot against GCS and cancels it when no object exists [ENG-4709] —
        // so this is expected self-healing, not a stuck note. The error-level
        // tripwire for an *actually* stuck slot lives in
        // resolveEmptySegmentHandler ('Failed to cancel empty-segment phantom
        // slot'). [ENG-4498]
        context.logger.warn(
          'Segment finalized with no local audio file — scheduling empty-segment resolution',
          payload,
        );
      }
    },

    onPaused: enqueueActions(({ enqueue, context }) => {
      const totalRecordingDurationMs =
        context.totalRecordingDurationMs +
        (new Date().getTime() - new Date(context.recordingStartedAt).getTime());

      enqueue.assign({
        totalRecordingDurationMs,
      });

      enqueue.emit({ type: 'paused' });
    }),

    sendStopMessageToMediaRecorder: sendTo(
      MEDIA_RECORDER_ACTOR,
      () =>
        ({
          type: 'recorder.stop',
        }) satisfies MediaRecorderEvents,
    ),

    // Force a monitor tick now (vs. waiting for the interval). Used on state
    // entries that need a fresh decision before the user can act.
    requestStorageCheck: sendTo('storageMonitor', { type: 'forceCheck' }),

    onClientAudioCompletedAt: enqueueActions(({ enqueue, context }) => {
      const clientCompletedAt = new Date().toISOString();
      if (context.recordingFileName && context.sessionAppointmentId) {
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT',
            appointmentId: context.sessionAppointmentId,
            clientCompletedAt,
            fileId: context.recordingFileName,
          },
        });
      }

      if (context.lastObservedChunkFileName && context.sessionAppointmentId) {
        enqueue({
          type: 'scheduleBackgroundTask',
          params: {
            type: 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT',
            appointmentId: context.sessionAppointmentId,
            clientCompletedAt,
            fileId: context.lastObservedChunkFileName,
          },
        });
      }
    }),
    onRollingToNewChunk: enqueueActions(({ enqueue, event, context }) => {
      assertEvent(event, ['fileSystem.rollingToNewChunk']);
      enqueue({
        type: 'scheduleBackgroundTask',
        params: {
          type: 'TASK.MARK_CLIENT_AUDIO_COMPLETED_AT',
          appointmentId: context.sessionAppointmentId!,
          clientCompletedAt: new Date().toISOString(),
          fileId: event.completedChunkName,
        },
      });
    }),

    onPausing: enqueueActions(({ enqueue }) => {
      enqueue.assign({
        isPausing: true,
      });

      enqueue({
        type: 'logMessage',
        params: {
          level: 'info',
          message: 'Received pause event',
        },
      });

      enqueue('sendStopMessageToMediaRecorder');
      enqueue.raise({ type: 'CLIENT_AUDIO_COMPLETED_AT' });
    }),

    onStopping: enqueueActions(({ enqueue, event }) => {
      assertEvent(event, ['STOP']);
      enqueue.assign({
        isStopping: true,
      });

      enqueue({
        type: 'logMessage',
        params: {
          level: 'info',
          message: 'Received stop event',
        },
      });

      enqueue('sendStopMessageToMediaRecorder');
      enqueue.emit({ type: 'stopped' });
      enqueue('logStartStopEvents');
      enqueue.raise({ type: 'CLIENT_AUDIO_COMPLETED_AT' });
    }),
  },
  delays: {
    userPresenceResponseWindow: ({ context }) => {
      return context.userPresenceResponseWindowMs;
    },
    userPresenceTimeout: ({ context }) => {
      return context.userPresenceTimeoutMs;
    },
    finalizingAudioTimeout: ({ context }) => {
      return context.finalizingAudioTimeoutInMs;
    },
    firstFileWatchdog: FIRST_FILE_WATCHDOG_MS,
    chunkFileWatchdog: ({ context }) =>
      (context.chunkDurationMinutes ?? 5) * 60_000 + CHUNK_WATCHDOG_SLACK_MS,
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QBUBOBDAdrAxqglgA4Au+A9pgHQCSEANmAMQDKyAggErIDaADALqJQhMrHykKQkAA9EAWgCcvAKyUAjADYA7AA4tAFn1aDO5QBoQAT3n6llHQoUBmLQs0AmHRodaAvr4s0LFwCEnIqWgYWdi5uNUEkEBExCUwpWQRFXl5KfWUNZX1eW3citQULa0zlLTV1JxV3LSc1TQ0nBX9AjGw8IlSaeiY2AFUAEWoAeQB9ADU2ABlqMbZkKYA5aY4AUQBFEe3WbbG+BOFRcXD0+TVsjUpPJxd3UtMnJsrEZRf7B3db9zfJoGLogIK9UIDaiYZhwMQUSgcMA4MioCD4TBQRg7ADCk3W622OJ4AikyUukkSGX07icD3KpV4-ycGhp5isiFa2UoujUpmKTmUTwUylB4JC-XCNBhcKlSJRaIxWIAZvgGMxLLBiGAALaUVUMJHoCCWU5ki6pa6ZNSCnkqZRcjoKAyfBBqQz3Xg6fRefR8l4FdxinoSsII6Gw2Dwqjy1HozGMA1gDVa3WUVBkOh0JXIMjrMAAdxxAAsAK6YADWZsS5MtVMQLLpOhZ7ReagdOmKrs0Oh0lAUeS0hVZBSZOmDwT6YYiMqjcuRcaVibVyc12r1SZxqDA6G1EGr5xSV3rCBe+nUCg0j2+rnyTh03f0BUoV-cV94rIdjgnEMl4dn0aIguioJjiSzbOsyDTKMEwzHiACyAAKCzbMgxzQSSZxJBax6gFUcjKDUPLaDoajuLwWjaEy+gWBkTRCi+nYFJeTK8AozY-qGUIAfOCrxlAlDwZA+DoLGaJgKgjDbnxEmUBJGaoAe2FHpSoAZKyL7ZHoOhvqylEuK6cjNPYJSDrwgqClenFTtxkaAWJ-GCcJonARAEmMDq+B4GQhDFhQYCUOiuAUJgyJ7kpta4TIiC0go-aeM4zbtkYDiGfodLugOXrti0naigEYIhjZUoRrKCIOUqTnoi5MmSZ53m+f5lA6qW4WkjWOGqdFbo+u4lBCt6ti2BoGjOuy+HtnUxQ2moWj-K0hRetZkIlTx5WuZVQnVQ57n1RmjWhZQ5YtW1WGRV1GTusZT5MkOKiOLcGiGZ4OQaLwZFkbUDTKM4+jLX+M52bxi6YlVIk7aglALGQxpLtJcayVq6CoKd5oqWkJ4ilo-XuG4FHeh0twVBy1peOo702m+A4uKR44FeKxX-kD618ZVbCluiZAAGIrim67Luqa5pvJqIRZ1GNqYgRg5M0A6jexgoOu2rqdrk3i6CKct+k4-3TtKzMxhtoPs5zPOC6merQ7DCZJnzabbsaprtYeFIS91IqqN83x+gYzpvTRJPuvco1Ck8Qq1MYLK67ZZWG6zoMAOpgAARrAZA4BWYDEJQJbIhWSrUMq2yYOgycMBAjBi+jVq3E457h2ydduN4T0kzpfbOBorQRzal5qNHq0G0B8cCUnqfp5n2e5xnBdFyXZeQJX8Ro67NeCnUPojXX5GUexKueP2LLd7NvdWfTRUrUzsfDyDo8p2nGdZ5Qxel+XjAopgoU4AMxAX-06bIjAPgAAbpAKuq8TytFVvpdiuhbA9lblUd0+R1CFDlrUZsnRz6TkvoDa+FVE73wnk-F+C8K4fy-gMIKFCwqQCOqFaQhBaH0CdmdcWNcbw8k8KRMOTwdKPlGi+FoyhzKK1ZFg7oOCAb63wUbO+49H7Z1IW-Gh38pSwDAJgCAJtyCIXQIo-U6AVz7mdspCBks3QDmxu9NwzYRpdx8AIuK7RNCFEMO3H6A8r5zhZrfSgY8H6T2fvPFRIUwpSnLOgYBRi6CvzAOAusFi9B9U3uImo94yLuEfM2cmeVHAKFxl6Pw2Dfx61Kj4uOfiAnEKUSEuh7NiDFk0aQHAu4pRW34owCATUMTALIJnSgDNcEyIqTfEC8jAkkLqRASgDSmmYBaW0hEHSlQIF6enJZmBTgJKipdRaDxSgiheB+WaThuxNA7hRWwTwg51y8Xg0ZBCJk1OCXEmZczmleU2VDGGnSRaQ0ILE4gypUR6iGdI8p9k5H+KIQY5R9TWrzMWQMFZmI1mYD6a01I2zTHnTdpdVo54mR8neHkDoo0smBxtNjFu5FASkRtO9e5IyoUjxhQooJ8KZk4jCWo5ZvylyqIGMqGJYDcXsJPORXsL5aSCgwV6NiD4qU0nUG+H63hOxuCFMyyFwNxnssmbUt5OdeUooFQmIVUohVirYdXSVxR7gdFxq0Vk-xzKUqQYCbGzYhxeFgUUOmkjSkx0edCkYGjUCIW3BozAOAAqTArOgSwjBpBI21JQdAyptSoAABSlgjVGuAmi43IHwDqMAZBWoAEpGDgrKWtSp+rw0SULTGuNlAE1Jp2RdTk3psbGF0Gq5o8CPVSyKLkJoY4Rp6BaCoHVDaxmOWbZG6NxaArT3zqBfEax1gHG7fimKLRsYOi9L2bw7FtAByqLje4tQPY0hcGRdo86h5PMoMu1ta6c5NJngmRCoxmDbH3TXNwRLfo+tZGSpVVQryeh4TY8y3g-QvtkWyj9q7Y3rp-ZurEqbiC7gCpm7NeaC0YbjUiWAIhsBgAThibpBYa11pDayvx6Gi2Ye-XnJUwHIF5HPHKwENQ2J8hGq6LuqgaRDX5KRGkxSg1cUHqhvxiFNH8XzNIYgJZywVkGFEW2QsNwri3DuVGHU7UWJeHNfqu8vTtC8O6Z6TQHjOlsIO2zHR8rycZg8lj+qVNaKVOpzTZZKyUCjWQYB+BoyBbABprTlYuk9Ixf0gKTHFOhrZf5tTsXgvabCxmSL0XMRBfixWdFmLNk4tteY7q5EyIPFqHcBo5lmzjRuA0fsXohq6DmnyJkKGMvKdUzFuLIWdPhcK+EEbuWEv-MoIC3cILUBgr-vW190KsvTdK-liLUWpvFZy6V8rGzsUCB4xY2aBhKAuCfHXClRS2sIByEyAMAofSGDYiKVkA3fOOR5iXbMAAvJUTypKuVkjgBgWBSyEGmKqAH+BAc2pXok7qQc+yeFGoTOzShEENhEZQYoxhCi6DroYOThUpFraU-q-76Agcg+hZEJg-6RiAfO2j8odIXhNCMCKGkOkzkk3vPcANhEbTqx+v3EpCnvG-cqnThnmI33M+iJMRCHOMiRwa+9x4zolBaFdCL3IXpxcsj0D9OdMvvMsr1X9jE9PEeM7ZYrp3CZNdS0ooT50jxHiPtdN8GWo1Zo+ku1eCnaW5d24Vw7pXUA32u+B+75eZmavUj0P2LGk1zLfAMiTDBMrgSC-ydoH70fQaJ+d34yvCY8MEYzVmiSOb4eO6T1AHRZBS3lsrcQRjq3mPl4EjX+P0Lh8e4QFobk6TCJOgdNcsTFFiLN1fJ2YoQZrfDN1b42nse3cj5d7vtvKa02Ecb7mlvceO9d4rdW2t-f0vy4r4fqvO+Edt7iNV1Hl02KE9ZHoX1Dik+V6iAEc9gc0b07w7EpEb4ZeCIei+ai8OwzAIw8EQG4q5m3UcgfoOQbgZEpKtIBQXgj2TQt6T4zQXc5kWkz6G+EKC68BGiFcrA6u4+cgW83uTwxg3oIiNIeObobEfUb4tQgImSj6sBVA9Bi8EwzA8E1AzAzA0wgGchGwLB-oqCtIvYNIzocCQuVQfo54XcQ4T4Iixg+uYhi6SoXMoqFcLBDQdQ9mj6jgd4H4OhiAV4PImgLILEIitc7oZhTylhxiS8n+uy8gwIqCguygzY94+QLogc3w5MIoDQxOC0TQZhBsxcbk1h6Bae8gn412LgbglkDgBQKsKqFk7YVyzQySZhAAQmAEtmAKDiwRpKbnkCoKRHPoKIZPkH2L2I0COHdpoLUfUaiI0XIkESjiEZkG+P1OZIYK8FpP8DUGlDMaNFeDdO3M4ICGYYhLEpYEqGMFFpDkYuWpJMhGwAAJqUBSFgRsDUCoEcDPzrBjDHDj6xTxStADikRwKC4rEdxPilBSptjGB-Q0HU6DafzhLu7ZFf5fDxFXimBGBDhCi4zdh8gbyT4-Q6TGC+FgkD5wHbiEDIwv6dLdKHTrIDKR4+ZSiFpEkEDK5yLHZYrhBVaTE9qngqD9RrE+z-A6R3iGTlDBxeiXhjT8FuA7GEnEkMkjyMBzYLbAqgqDL35R4ElgB0kkmrLrLMkUCsmp6wkcn3C0i+qGD84ODAGZC5TqCUQxE9H6TMqzCO4QBtKYgd44joAkCljbiMAwRTBzCLDLCrAbBbB7AHBHAnAwlTFyAfSOoOjWk+gdB1wuET4aS5TODZC2AKyBqU7BpSgOnZhOmkAukczkBukelenoDFlkCUBpzlgQBjBZzMIsE+DXb64+gGCREnxiY-A2IdmGD5BdzS5ebDJ5n4AFlKiununECelMAVmczVkrgLIqHlCqBdykosj0RJkZn9hPpbxsQGA2j2mOnOnt6VmllTlek4hsDrA4jbALB+lLArBrD4gsFKBxReAqDtmlD3g0gqyRHXZ8hpnFCODNh0wFSYBkBuTwCJBUlskHrWgtDnj-BGAAmRwOYkxyCDT1DZTaBAhUw6pDCwVWhGTSrpTrwGAgnIXPTFDqB8iERXjfm4zNBiGEUnhGStDsEuBwLcFviGRDj3CXhFClBvSfRRx4kP6D4sUWLaD3BeCURrFY7vDdFESUTGDCUJlYx+HQpbTgzg6oCSXdRzSqBAHvSCa4x5DQbyAtBxS-QBraDqHDSaVsraU1QIyQyopQD6Va7GTGVkSERmWRECntCqrmWFBS7yyOV+LOUQzmGYieWcjkSIVKAODeivAOiPZRlBVkQhV5DNzfhiUqmNqOQd5myrgWxxUT6lD2DSXyXOCKWBx9T2bvknzZAvAFIRX6rFW8wGY-LWweV6lTHGANXVWXgKWjo9TByJG8IRzkHtVFWVklV2x6gJwECFl9Uuz6kiGE4JVvQyZFBJlBwviTUWTTWiVDm0HrZsrVKKLlVzRvm3CmA-Qz7vC8FVEPAUqsjCbpS4yzWVRXVBIbqzxco3XGD2D3WEQexPA8VtyrH2aXgYIdA-WEIcpPyHGwBvLlXtiOAPD5CzTgYnoWUIB8m5AsgqDrHqFdyI3PJwrTI3Wqxw2tCeDwItyujNyMSBhMh3izTr5nXgmP5U2crTKzKIqfLalux4pWjtz9jaAM3ejNxeDdgqA5BfiuD0VfWgk834mFW-WwoC3GofILJfJmq9XlV6D8XS1kSy0IJom2D1BbEjRF6USU0GovJcpC2NIi3fIBHlwY2eE8jypzFy28Hdx9TtCdzaRPAUQSLZmy7Unb6OR-VTLGo8qQl8pi0SoXaUREqZ3aRM3y2BwqDnjpSsjlB2J8YR7Kmx1a1I2GqvJkImop1G38QY3QGE7Z1wKB3W11AQHzT+pLT5WV0xX82J113J2UJShe2QDlUEH8ZvhtmW3M1UrPi1yAh6G4zcJO0J1Goj2mpSij3MJT3vAY7vDNDt1W31VEQ+qRGjR6ABpO1sZtpgAY3DpvXmU8J5TGCujkRxR+WURSoGEtB32kbsbtqdpVDrVTGzQ6Qv2RFv0fmG7C7lCHVXiEoPROgaCAMtpkZYZcaxX9XsnzQNVuK2DmQG4fAkx+gSZUz3ikQDlCiebR025b5V0CSbYHajbaZP0zE3btBPgFIPaGQIVqxFDOgfguanUMOb4LpvqsNQAlZja6aP14NwUAU5DH2dgq30p1xUUz1db2i9giLiNUm25x2VQyNyN5YTZ7YUBbZjZT18jYxKA-QegOB3Bom5BDjfmh7eFGlO3D5PI3WAg64pWYKuAUSf1Y2lCzQ-T-C6DtH0NGNMOD2UB+NM4EVKM1zZB0iBh8I-TH1sSujCPXamA2gNCDSFA6z93GPMPJPP7SnV61NrVmL6nsSqCWZfHCVQJjUiKqD2gnpHI6RDGVOJMSEQAH0pL7kdFr6Amf1ky2GJQ0MpHq0SPnU078QT2jPpOsWGD9TtgOBjTdMeKuhWbmRkTOAjV9ql5DMLrpFaKT2bMWJyB1x1CXaDVGAsjwNVDfHXYomfE3190a3iUIh1ENH+P3OYGH25BuBCHPWkUfPyA1DPPfRnjh72Y7F7EHFHGxJloSRT0220iRFeEGDtDpVCb9TvOURcFXTxMV1VM3wN1KjlVhyE7cUdFeANDExIK439iT5Ohtm31XNDy0lSn763zlVYEfioKjSKrvjlAfgCPjqxQfjZAjXhyHn5nHkTllmKPgPslRm6T0jIXh7NBoUwZeiQsDkF3Sw6T+D+BAA */
  id: 'Transcription',
  initial: 'Idle',
  context: ({ input }) => {
    // Legacy support for the fact that this machine allowed
    // undefined audio formats
    const supportedAudioFormat = (() => {
      try {
        return getSupportedAudioFormat();
      } catch {
        return undefined;
      }
    })();

    let finalizingAudioTimeoutInMs = 0;
    if (input.finalizingAudioTimeoutInMs === undefined) {
      finalizingAudioTimeoutInMs = input.isUsingWebWorkerAudioRecording
        ? FINALIZING_RECORDING_TIMEOUT_FOR_WEBWORKER_MS
        : FINALIZING_RECORDING_TIMEOUT_MS;
    } else {
      finalizingAudioTimeoutInMs = input.finalizingAudioTimeoutInMs;
    }

    return {
      ...input,
      audioChunkFiles: [],
      recordingRetryCount: 0,
      recordingFailed: false,
      supportedAudioFormat,
      recordingStartedAt: new Date().toISOString(),
      userPresenceResponseWindowMs: INITIAL_USER_PRESENCE_RESPONSE_WINDOW_MS,
      userPresenceTimeoutMs: INITIAL_USER_PRESENCE_TIMEOUT_MS,
      confidenceScoreWindow: [],
      isLowConfidenceTranscription: false,
      recordingFileName: '',
      resumableUploadSessionUri: undefined,
      nextChunkResumableUploadSessionUri: undefined,
      filesPendingCompletion: [],
      totalRecordingDurationMs: 0,
      isRecordingCleanupFinalized: false,
      isPausing: false,
      isStopping: false,
      hasValidAudioSource: undefined,
      segmentAudioFileObserved: false,
      finalizingAudioTimeoutInMs,
    };
  },

  invoke: {
    src: 'monitorRecordingStorage',
    id: 'storageMonitor',
    input: ({ context }) => {
      const recordingMode = context.isUsingWebWorkerAudioRecording
        ? resolveAudioSourceMode(context.useBlobMode)
        : 'blob';
      return {
        enabled: context.isStorageBlockEnabled ?? false,
        chunkDurationMinutes: context.chunkDurationMinutes,
        bitrateBps:
          recordingMode === 'pcm' ? context.opusBitrate : BLOB_MODE_BITRATE_BPS,
      };
    },
  },
  on: {
    storageDecisionResolved: {
      actions: enqueueActions(({ enqueue, event, context }) => {
        assertEvent(event, 'storageDecisionResolved');
        if (event.decision === context.storageDecision) {
          return;
        }
        // Log only on transition INTO block — keeps polling logs low-noise.
        const transitioningToBlock =
          event.decision === 'block' && context.storageDecision !== 'block';
        enqueue.assign({ storageDecision: event.decision });
        enqueue.emit({ type: 'storageDecisionChanged' });
        if (transitioningToBlock && event.storageLogFields) {
          context.logger.info('Recording storage decision changed to block', {
            appointmentId: context.sessionAppointmentId,
            deviceId: context.appDeviceId,
            decision: event.decision,
            ...event.storageLogFields,
            chunkDurationMinutes: context.chunkDurationMinutes,
          });
        }
      }),
    },
    // Keep the persistent machine's tenant in sync when the active tenant
    // changes without a remount (browser back/forward, direct param change).
    // Without this, a reused machine authenticates the next live-transcription
    // WebSocket with a stale `socket.auth.tenantId` → server rejects it →
    // degraded transcription.
    UPDATE_TENANT: [
      {
        // Same tenant: nothing to do (avoids tearing down an in-progress
        // recording on benign re-renders that re-send the current tenant).
        guard: 'isSameTenant',
      },
      {
        // Tenant genuinely changed mid-session. Adopt the new id for the next
        // recording, then raise STOP so the in-progress recording finalizes
        // through the normal FinalizingRecording → SessionEnded → Idle path,
        // which waits for the recorder to flush (`recorder.cleanup_finalized`)
        // before `stopRecording` schedules the upload. A hard jump to `.Idle`
        // here would skip that wait and truncate the old tenant's audio / drop
        // its final upload. Safe ordering: `stopRecording` keys on
        // `sessionAppointmentId` (not `tenantId`) and the recorder actor already
        // captured the old `tenantId` at invoke, so adopting the new id now does
        // not misroute the old recording's upload.
        guard: 'hasActiveSession',
        actions: ['updateTenant', raise({ type: 'STOP' })],
      },
      {
        // Idle: just adopt the new tenant id for the next recording.
        actions: 'updateTenant',
      },
    ],
  },
  states: {
    Idle: {
      entry: [
        emit({
          type: 'idle',
        }),
        // Fresh check on re-entry (after STOP / DISMISS_SESSION); initial
        // tick comes from the monitor's own startup.
        'requestStorageCheck',
      ],
      on: {
        START: [
          {
            target: 'Idle',
            guard: 'hasNonSupportedBrowser',
            actions: [
              {
                type: 'toast',
                params: {
                  type: 'error',
                  message:
                    'Unsupported Browser: Eluve supports Chrome, Safari, and Edge for live transcription.',
                },
              },
              'captureException',
            ],

            description: `If recording isn't supported we should alert the user and transition back to an idle state`,
          },
          {
            // Defense-in-depth behind the disabled start button. Silent
            // refusal — disabled button + tooltip is the user signal.
            target: 'Idle',
            guard: 'hasInsufficientStorage',
            description: `Refuse to start when free storage is below the critical threshold`,
          },
          {
            target: 'InSession',
          },
        ],
        AUDIO_VALIDATION_REQUESTED: 'ValidatingAudioCapture',
      },
    },

    InSession: {
      entry: ['setSession', 'setupMediaStreams'],

      // [ENG-4816] At InSession (not Recording) so the lock survives reconnects.
      invoke: {
        src: 'keepScreenAwake',
        input: ({ context }) => ({
          logger: createCorrelatedLogger(context.logger, {
            appointmentId: context.sessionAppointmentId!,
          }).log,
        }),
      },

      // [ENG-4498] Handled at the InSession level (not just Recording) so a
      // health check that passes in a sibling substate — e.g. Reconnecting —
      // still stamps the last-known-good time. Otherwise the sleep-gap window
      // (errorDetectedAt − lastKnownGoodHealthCheckAt) can over-estimate.
      // Stamping is idempotent and safe in any substate.
      on: {
        'microphone.successfully_checked_audio': {
          actions: ['recordHealthyAudioCheck'],
        },
      },

      states: {
        Recording: {
          invoke: [
            {
              src: 'preventBrowserClose',
            },
            {
              src: 'lockManagerActor',
              id: LOCK_MANAGER_ACTOR,
              input: ({ context }) => ({
                logger: createCorrelatedLogger(context.logger, {
                  appointmentId: context.sessionAppointmentId!,
                }).log,
              }),
            },
            {
              // Async provisioning of upload records + resumable session. The
              // recording is already running; on resolve the session URI is
              // late-attached to the current chunk (worker backfills from
              // OPFS), so nothing is lost by not blocking on this. The 15s
              // timeout replaces the old 3s start-gating budget — generous,
              // because nothing waits on it anymore.
              src: 'prepareAppointmentAudioRecords',
              input: ({ context }) => ({
                tenantId: context.tenantId,
                appointmentId: context.sessionAppointmentId,
                fileName: context.recordingFileName,
                useResumableUploads: context.isUsingResumableUploads,
                userAgent: navigator.userAgent,
                microphoneName: context.microphoneName,
                metadata: buildSegmentMetadata(context),
                timeout: 15_000,
              }),
              onDone: {
                actions: [
                  assign(({ event: { output } }) => ({
                    ...output,
                  })),
                  enqueueActions(({ enqueue, event, context }) => {
                    const {
                      resumableUploadSessionUri,
                      nextChunkResumableUploadSessionUri,
                    } = event.output;
                    if (resumableUploadSessionUri) {
                      enqueue.sendTo(MEDIA_RECORDER_ACTOR, {
                        type: 'resumable_upload_session_uri_available',
                        fileName: context.recordingFileName,
                        sessionUri: resumableUploadSessionUri,
                      } satisfies MediaRecorderEvents);
                    }
                    // The worker was initialized before prepare resolved, so
                    // chunk 1's session must be delivered by message too.
                    if (nextChunkResumableUploadSessionUri) {
                      enqueue.sendTo(MEDIA_RECORDER_ACTOR, {
                        type: 'next_chunk_resumable_upload_session_uri_available',
                        nextChunkResumableUploadSessionUri,
                      } satisfies MediaRecorderEvents);
                    }
                  }),
                ],
              },
              onError: {
                actions: [
                  'captureWarning',
                  enqueueActions(({ context, enqueue }) => {
                    if (context.sessionAppointmentId) {
                      enqueue({
                        type: 'scheduleBackgroundTask',
                        params: {
                          type: 'TASK.PREPARE_AUDIO_RECORDS',
                          appointmentId: context.sessionAppointmentId!,
                          fileName: context.recordingFileName,
                          useResumableUploads: context.isUsingResumableUploads,
                          origin: context.clientOrigin,
                          userAgent: navigator.userAgent,
                          microphoneName: context.microphoneName,
                          metadata: buildSegmentMetadata(context),
                          deviceId: context.appDeviceId,
                        },
                      });
                    }
                  }),
                ],
              },
            },
          ],

          on: {
            RECONNECT: 'Reconnecting',

            'fileSystem.fileReady': {
              actions: 'processCompletedChunk',
              description: `A file has been finalized and is ready to be sent off to STT processor to be transcribed`,
            },

            'fileSystem.rollingToNewChunk': {
              actions: 'onRollingToNewChunk',
            },

            'fileSystem.fileCreated': {
              actions: 'fileCreated',
              description: `A new file has been created which will need to be eventually uploaded. This could be a single batch file for an entire recording or files that are chunked as the recording progresses.`,
            },
            'fileSystem.chunkingDegraded': {
              actions: 'onChunkingDegraded',
              description: `The worker's chunk remuxer failed and capture degraded to the raw batch file — chunk files stop arriving by design, so disarm the chunk watchdog.`,
            },
            'recording.fileWatchdogTimeout': {
              target: '#Transcription.InSession.RecordingFailed',
              actions: 'onFileWatchdogTripped',
              description: `No fileSystem.fileCreated arrived within the expected window — the recording worker/pipeline is wedged and audio is silently not being persisted. Fail the recording so the retry path (fresh worker) engages instead of recording nothing.`,
            },
            CLIENT_AUDIO_COMPLETED_AT: {
              actions: ['onClientAudioCompletedAt'],
            },
          },

          entry: [emit({ type: 'recording.started' }), 'armFileWatchdog'],
          // logSegmentFinalized runs before stopRecording so it can read the
          // segment's audio-observation state before stopRecording clears it. [ENG-4498]
          exit: ['cancelFileWatchdog', 'logSegmentFinalized', 'stopRecording'],

          // Pause when OPFS storage drops below threshold mid-record.
          always: {
            guard: ({ context }) =>
              context.storageDecision === 'block' &&
              !context.isStopping &&
              !context.isPausing,
            actions: enqueueActions(({ context, enqueue }) => {
              context.logger.warn(
                'Recording paused mid-session due to insufficient storage',
                {
                  appointmentId: context.sessionAppointmentId,
                  deviceId: context.appDeviceId,
                  totalRecordingDurationMs: context.totalRecordingDurationMs,
                },
              );
              enqueue.emit({ type: 'recording.stopped-by-storage-block' });
              enqueue.raise({ type: 'PAUSE' });
            }),
          },

          states: {
            MediaRecorder: {
              invoke: {
                id: MEDIA_RECORDER_ACTOR,
                src: 'mediaRecorder',

                input: ({ context }) => {
                  const { log } = createCorrelatedLogger(context.logger, {
                    appointmentId: context.sessionAppointmentId!,
                  });

                  return {
                    tenantId: context.tenantId,
                    userId: context.userId,
                    appointmentId: context.sessionAppointmentId,
                    recordingStartedAt: context.recordingStartedAt,
                    initialFileName: context.recordingFileName,
                    supportedAudioFormat: context.supportedAudioFormat!,
                    audioPacketSubject: context.audioPacketSubject!,
                    deviceId: context.deviceId,
                    logger: log,
                    db: context.db,
                    MuteDetector,
                    resumableUploadSessionUri:
                      context.resumableUploadSessionUri,
                    nextChunkResumableUploadSessionUri:
                      context.nextChunkResumableUploadSessionUri,
                    chunkDurationInSeconds: isChunkedBatchActive(context)
                      ? context.chunkDurationMinutes
                        ? context.chunkDurationMinutes * 60
                        : undefined
                      : undefined,
                    datadogEnv: context.datadogEnv,
                    serviceName: context.serviceName,
                    useBlobMode: context.useBlobMode,
                    opusBitrate: context.opusBitrate,
                  };
                },
              },

              states: {
                Loading: {
                  on: {
                    'recorder.started': {
                      target: 'Recording',
                      actions: 'setMediaStream',
                    },
                  },
                },

                Recording: {
                  on: {},
                },
              },

              initial: 'Loading',

              on: {
                // QuotaExceededError from OPFS is a storage-cap event, not a
                // recording fault — emit storage-block + raise PAUSE so audio
                // flushes and the session lands in Paused rather than completing
                'recorder.error': [
                  {
                    guard: ({ event }) =>
                      event.error?.name === 'QuotaExceededError',
                    actions: enqueueActions(({ context, enqueue }) => {
                      context.logger.warn(
                        'Recording paused: OPFS write threw QuotaExceededError',
                        {
                          appointmentId: context.sessionAppointmentId,
                          deviceId: context.appDeviceId,
                          totalRecordingDurationMs:
                            context.totalRecordingDurationMs,
                        },
                      );
                      enqueue.emit({
                        type: 'recording.stopped-by-storage-block',
                      });
                      enqueue.raise({ type: 'PAUSE' });
                    }),
                  },
                  {
                    target: '#Transcription.InSession.RecordingFailed',
                    actions: ['captureWarning', 'onClientAudioCompletedAt'],
                  },
                ],

                'microphone.disconnected': {
                  actions: [
                    'onMicrophoneDisconnected',
                    'onClientAudioCompletedAt',
                  ],
                  target: '#Transcription.InSession.Paused',
                  reenter: true,
                },

                'microphone.muted': {
                  actions: ['onMicrophoneMuted'],
                },

                'microphone.unmuted': {
                  actions: ['onMicrophoneUnmuted'],
                },
              },
            },

            AudioFileSystem: {
              invoke: {
                src: 'audioFileSystem',

                input: ({ context }) => ({
                  appointmentId: context.sessionAppointmentId!,
                  fileExtension: context.supportedAudioFormat!.extension,
                  userFileSystem: context.userFileSystem,
                  audioPacketSubject: context.audioPacketSubject!,
                  recordingStartedAt: context.recordingStartedAt,
                  db: context.db,
                  chunkedBatchTranscriptionEnabled:
                    isChunkedBatchActive(context),
                  chunkDurationMinutes: context.chunkDurationMinutes,
                  logger: createCorrelatedLogger(context.logger, {
                    appointmentId: context.sessionAppointmentId!,
                  }).log,
                  numAudioPacketsConsumedByLocalFile: 0,
                  totalBytesConsumedByLocalFile: 0,
                  initialFileName: context.recordingFileName,
                }),
              },

              states: {
                Loading: {
                  on: {
                    'fileSystem.ready': {
                      target: 'Writing',
                    },
                  },
                },

                Writing: {},
              },

              initial: 'Loading',

              on: {
                'fileSystem.error': {
                  target: '#Transcription.InSession.RecordingFailed',
                  actions: 'captureWarning',
                },
              },
            },

            Websocket: {
              initial: 'CheckingIfEnabled',

              states: {
                CheckingIfEnabled: {
                  always: [
                    {
                      target: 'Enabled',
                      guard: 'isLiveTranscriptionEnabled',
                    },
                    {
                      target: 'Disabled',
                    },
                  ],
                },

                Disabled: {
                  type: 'final',
                },

                Enabled: {
                  invoke: {
                    id: WEBSOCKET_ACTOR,
                    src: 'websocket',

                    input: ({ context }) => ({
                      tenantId: context.tenantId,
                      appointmentId: context.sessionAppointmentId!,
                      format: context.supportedAudioFormat!,
                      segmentId: context.segmentId!,
                      audioPacketSubject: context.audioPacketSubject!,
                      recordingStartedAt: context.recordingStartedAt,
                      numAudioPacketsConsumedByWebSocket: 0,
                      totalBytesConsumedByWebSocket: 0,
                      logger: createCorrelatedLogger(context.logger, {
                        appointmentId: context.sessionAppointmentId!,
                      }).log,
                      apiDomain: context.apiEndpoint,
                    }),
                  },

                  initial: 'Authentication',

                  states: {
                    Authentication: {
                      states: {
                        Loading: {
                          entry: [],
                          invoke: {
                            src: 'getAuthToken',

                            onError: {
                              target: 'Failed',
                              actions: [
                                'setTranscriptionDegraded',
                                'captureException',
                              ],
                            },

                            onDone: {
                              target:
                                '#Transcription.InSession.Recording.Websocket.Enabled.Connection',
                              actions: [
                                assign({
                                  authToken: ({ event }) => event.output.token,
                                }),
                              ],
                            },
                          },
                        },
                        Failed: {},
                      },

                      initial: 'Loading',
                    },

                    Connection: {
                      states: {
                        Loading: {
                          entry: 'startWebSocket',
                          on: {
                            'connection.failed': {
                              target: 'Failed',
                              actions: ['setTranscriptionDegraded'],
                            },
                            'connection.connected': 'Connected',
                          },
                        },

                        Failed: {},
                        Connected: {},
                      },

                      initial: 'Loading',
                    },
                  },

                  on: {
                    'connection.transcript.received': {
                      actions: [
                        'updateTranscription',
                        'updateLowConfidenceState',
                        emit(({ event }) => event),
                      ],
                    },
                    'connection.disconnected.unexpectedly': {
                      actions: ['setTranscriptionDegraded', 'captureWarning'],
                    },
                    'connection.sendAudioPacket.failed': {
                      actions: ['setTranscriptionDegraded', 'captureWarning'],
                    },
                    'connection.unavailable': {
                      /**
                       * In degraded mode, we don't send audio packets to the server.
                       *
                       * TODO(deep/sargis)[ELU-1503]: Remove this workaround once the issue is resolved.
                       * On Chrome, specifically in a session popout, the sound bars are not rendered as the user speaks when in degraded mode.
                       * This issue does not occur in Safari; it works fine there.
                       * This seemingly redundant assignment forces the Waveform component to render the bars correctly in Chrome.
                       * There might be a better solution to this issue.
                       */
                      actions: assign({}),
                    },
                  },
                },
              },
            },

            UserPresence: {
              states: {
                Okay: {
                  after: {
                    userPresenceTimeout: { target: 'Checking' },
                  },
                },
                Checking: {
                  entry: [emit({ type: 'checkingUserPresence' })],
                  after: {
                    userPresenceResponseWindow: {
                      target: '#Transcription.InSession.Paused',
                      actions: 'resetUserPresenceDelays',
                      reenter: true,
                    },
                  },
                  on: {
                    CONTINUE: {
                      target: 'Okay',
                      actions: 'shortenUserPresenceDelays',
                    },
                    PAUSE: {
                      target: '#Transcription.InSession.Paused',
                      actions: ['resetUserPresenceDelays'],
                    },
                  },
                },
              },
              initial: 'Okay',
            },

            PendingNextChunk: {
              initial: 'Idle',
              states: {
                Idle: {
                  on: {
                    'fileSystem.fileCreated': {
                      target: 'ProvisioningNextChunk',

                      guard: ({ event }) => {
                        return event.chunkNum > 0;
                      },

                      actions: 'fileCreated',
                    },
                  },
                },

                ProvisioningNextChunk: {
                  invoke: {
                    src: 'prepareAppointmentAudioRecords',

                    input: ({ event, context }) => {
                      assertEvent(event, 'fileSystem.fileCreated');

                      return {
                        tenantId: context.tenantId,
                        appointmentId: context.sessionAppointmentId,
                        fileName: event.fileName,
                        useResumableUploads: context.isUsingResumableUploads,
                        userAgent: navigator.userAgent,
                        microphoneName: context.microphoneName,
                        // Unlike the initial prepare (which runs before
                        // `recorder.started`), this call has the encoder's
                        // actual capture params; the backend segment upsert
                        // replaces metadata on conflict, so this restamps the
                        // segment with audioSampleRate/audioEncodeMode.
                        metadata: buildSegmentMetadata(context),
                        // Same budget as the initial prepare: recording is not
                        // gated on this call, and the actor default (3s) times
                        // out on slow machines/networks — each timed-out call
                        // still reserves upload records server-side while the
                        // client discards the URIs, so chunks lose their live
                        // resumable upload and reservations pile up.
                        timeout: 15_000,
                      };
                    },

                    onDone: {
                      target: 'Idle',
                      actions: [
                        enqueueActions(({ enqueue, event, context }) => {
                          enqueue.assign({
                            ...event.output,
                          });

                          const { nextChunkResumableUploadSessionUri } =
                            event.output;

                          const currentChunkNum = getChunkNumberFromFileName(
                            context.lastObservedChunkFileName ?? '',
                          );

                          if (
                            nextChunkResumableUploadSessionUri &&
                            currentChunkNum !== null
                          ) {
                            enqueue.sendTo(MEDIA_RECORDER_ACTOR, {
                              type: 'next_chunk_resumable_upload_session_uri_available',
                              nextChunkResumableUploadSessionUri,
                            } satisfies MediaRecorderEvents);
                          }
                        }),
                      ],
                    },
                    onError: {
                      target: 'Idle',
                      actions: [
                        'captureWarning',
                        enqueueActions(({ context, enqueue }) => {
                          if (
                            context.sessionAppointmentId &&
                            context.lastObservedChunkFileName
                          ) {
                            enqueue({
                              type: 'scheduleBackgroundTask',
                              params: {
                                type: 'TASK.PREPARE_AUDIO_RECORDS',
                                appointmentId: context.sessionAppointmentId!,
                                fileName: context.lastObservedChunkFileName,
                                useResumableUploads:
                                  context.isUsingResumableUploads,
                                origin: context.clientOrigin,
                                metadata: buildSegmentMetadata(context),
                                deviceId: context.appDeviceId,
                              },
                            });
                          }
                        }),
                      ],
                    },
                  },
                },
              },
            },

            FinalizingRecording: {
              states: {
                Idle: {
                  on: {
                    PAUSE: {
                      target: 'Finalizing',
                      actions: ['onPausing'],
                    },

                    STOP: {
                      target: 'Finalizing',
                      actions: ['onStopping'],
                    },
                  },
                },

                Finalizing: {
                  always: [
                    {
                      target: '#Transcription.InSession.Paused',
                      guard: 'pauseFinalized',
                    },
                    {
                      target: '#Transcription.InSession.SessionEnded',
                      guard: 'stopFinalized',
                    },
                  ],

                  after: {
                    finalizingAudioTimeout: [
                      {
                        target: '#Transcription.InSession.Paused',
                        guard: 'isPausing',
                        actions: 'logForcedFinalizeExit',
                      },
                      {
                        target: '#Transcription.InSession.SessionEnded',
                        guard: 'isStopping',
                        actions: 'logForcedFinalizeExit',
                      },
                    ],
                  },
                },
              },

              initial: 'Idle',

              on: {
                'recorder.cleanup_finalized': {
                  actions: [
                    assign({
                      isRecordingCleanupFinalized: true,
                    }),
                  ],
                },
              },
            },
          },

          type: 'parallel',
        },

        Paused: {
          // Force a tick now — closes the "Pause clicked, Resume still
          // enabled for a tick" window before the user can interact.
          entry: ['onPaused', 'requestStorageCheck'],
          // Only the mic-disconnect recovery validates the selected device, so
          // the user can't resume onto a dead/silent mic. A normal pause (e.g.
          // user-presence) must NOT hold the mic open or reset audio
          // validation, so we route on `microphoneStatus`. Validation lives in
          // a child state so re-validating doesn't re-run `onPaused` (which
          // accumulates elapsed recording time).
          initial: 'Deciding',
          states: {
            Deciding: {
              always: [
                {
                  guard: ({ context }) =>
                    context.microphoneStatus === 'disconnected',
                  target: 'Validating',
                },
                { target: 'NotValidating' },
              ],
            },
            // Normal pause — nothing to validate; mic is not reacquired.
            NotValidating: {},
            Validating: {
              entry: [assign({ hasValidAudioSource: undefined })],
              invoke: {
                src: 'validateAudioCapture',
                input: ({ context }) => ({
                  deviceId: context.deviceId,
                  logger: context.logger,
                }),
              },
              on: {
                AUDIO_VALIDATION_REQUESTED: {
                  target: 'Validating',
                  reenter: true,
                  description: `Re-validate whenever the user (re)selects a microphone, including the same one`,
                  actions: ['setDeviceId'],
                },
                ['audio.soundDetected']: {
                  actions: ['onAudioSoundDetected'],
                },
                ['audio.silent']: {
                  actions: ['onAudioSilent'],
                },
              },
            },
          },
          on: {
            RESUME: [
              {
                // Defense-in-depth behind the disabled Resume button.
                // Silent refusal — disabled button + tooltip is the user
                // signal. Internal (no target) — re-entering Paused would
                // re-accumulate totalRecordingDurationMs via onPaused.
                guard: 'hasInsufficientStorage',
                description: `Refuse to resume when free storage is below the critical threshold.`,
              },
              {
                target: 'PreparingRecording',
                reenter: true,
                actions: ['setDeviceId'],
              },
            ],

            STOP: {
              target: 'SessionEnded',
            },

            DISMISS_SESSION: {
              target: '#Transcription.Idle',
            },
          },
        },

        RecordingFailed: {
          entry: [
            {
              type: 'toast',
              params: {
                type: 'error',
                message: 'Recording failed',
                // In case failed retried multiple times, giving id will prevent duplicate toasts
                id: 'recording-failed',
              },
            },
            'incrementRecordingRetryCount',
          ],

          always: [
            {
              target: 'PreparingRecording',
              guard: 'isFirstRecordingFailure',
              reenter: true,
            },
            {
              target: 'Paused',
              actions: ['onRecordingFailed', 'captureException'],
              reenter: true,
            },
          ],
        },

        SessionEnded: {
          always: {
            target: '#Transcription.Idle',
            reenter: true,
          },
        },

        BeforeRecording: {
          always: [
            {
              target: 'PlayingDisclaimer',
              guard: 'needsToPlayDisclaimer',
              reenter: true,
            },
            {
              target: 'PreparingRecording',
              reenter: true,
            },
          ],
        },

        PlayingDisclaimer: {
          entry: 'playDisclaimerSound',
          on: {
            'PLAY.DISCLAIMER.ENDED': {
              target: 'PreparingRecording',
              reenter: true,
            },
          },
        },

        Reconnecting: {
          always: {
            target: 'PreparingRecording',
            reenter: true,
          },
        },

        PreparingRecording: {
          entry: [
            'prepareRecording',
            'logSegmentProvisioned',
            'logRecordingStorageCheck',
          ],
          // Recording starts immediately — prepareAppointmentAudioRecords runs
          // asynchronously inside Recording and late-attaches the resumable
          // session to the in-flight chunk when it resolves. Capture must
          // never wait on the server.
          always: { target: 'Recording' },
        },
      },

      initial: 'BeforeRecording',
      exit: 'clearSession',
    },

    ValidatingAudioCapture: {
      entry: [assign({ hasValidAudioSource: undefined })],
      invoke: {
        src: 'validateAudioCapture',

        input: ({ event, context }) => {
          assertEvent(event, 'AUDIO_VALIDATION_REQUESTED');
          return {
            deviceId: event.deviceId,
            logger: context.logger,
          };
        },
      },

      on: {
        AUDIO_VALIDATION_REQUESTED: {
          target: 'ValidatingAudioCapture',
          reenter: true,
          description: `Re-run validation if a new microphone is requested`,
          guard: ({ event, context }) => event.deviceId !== context.deviceId,
        },

        ['audio.soundDetected']: {
          actions: ['onAudioSoundDetected'],
          target: 'Idle',
        },
        ['audio.silent']: {
          actions: ['onAudioSilent'],
        },
        CANCEL_VALIDATION: {
          target: 'Idle',
        },
      },
    },
  },
});

export type TranscriptionMachineActor = ActorRefFrom<
  typeof transcriptionMachine
>;

export type TranscriptionMachineSnapshot = SnapshotFrom<
  typeof transcriptionMachine
>;
